import PostApprovalPlaidAttemptRecord from '../../records/post_approval_plaid_attempt';

export const STATUSES = [
  'offered',
  'link_ready',
  'processing',
  'uplifted',
  'no_change',
  'identity_mismatch',
  'failed_soft',
  'timed_out',
  'skipped',
  'unexpected_result',
];

export const TERMINAL_STATUSES = [
  'uplifted',
  'no_change',
  'identity_mismatch',
  'failed_soft',
  'timed_out',
  'skipped',
  'unexpected_result',
];

export class InvalidPostApprovalResponse extends Error {
  constructor(message) {
    super(message);
    this.name = 'InvalidPostApprovalResponse';
  }
}

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const NEXT_ROUTE_PATHS = [
  '/init/intro',
  '/init/locked',
  '/init/auth',
  '/init/auth_verify',
  '/apply/business_details',
  '/apply/initial_review',
  '/apply/auth',
  '/apply/auth_verify',
  '/apply/approved',
  '/apply/personal',
  '/apply/verification_review',
  '/apply/business_details_additional',
  '/apply/owner',
  '/apply/business_review',
  '/apply/equifax_allow_access',
  '/apply/financial_information',
  '/apply/payment_method_instant',
  '/apply/bank_review',
  '/apply/payment_method_selection',
  '/post_approval/offer',
  '/post_approval/processing',
  '/purchase/resume_cart',
  '/purchase/terms',
  '/purchase/phone',
  '/purchase/verify',
  '/purchase/require_bank_credentials',
  '/purchase/confirm',
  '/finish/unavailable',
  '/finish/virtual_card',
  '/finish/vip',
  '/finish/pending',
  '/finish/declined_on_reapply',
  '/finish/declined',
  '/finish/past_due',
  '/finish/restricted',
  '/finish/order_fail',
  '/finish/callback_api',
  '/finish/standalone_order_confirmation',
  '/finish/instore_order_confirmation',
  '/finish/order_confirmation',
  '/finish/approved',
  '/finish/error',
];

const renderableContinuations = new Set([
  '/post_approval/offer',
  '/post_approval/processing',
]);

export const isRenderableContinuation = (path) =>
  renderableContinuations.has(path);

const nextRoutePath = (nextRoute) =>
  isObject(nextRoute) &&
  Object.keys(nextRoute).length === 1 &&
  NEXT_ROUTE_PATHS.includes(nextRoute.path)
    ? nextRoute.path
    : null;

const isPreAttemptResponse = (json) => {
  const responseKeys = Object.keys(json);
  const routeKeys = isObject(json.next_route)
    ? Object.keys(json.next_route)
    : [];

  return (
    responseKeys.length === 2 &&
    responseKeys.every((key) => ['can_continue', 'next_route'].includes(key)) &&
    typeof json.can_continue === 'boolean' &&
    isObject(json.next_route) &&
    routeKeys.every((key) => key === 'path') &&
    (!Object.prototype.hasOwnProperty.call(json.next_route, 'path') ||
      typeof json.next_route.path === 'string')
  );
};

const invalidResponse = (message) => {
  throw new InvalidPostApprovalResponse(message);
};

const safeObject = (value, fields, label) => {
  if (
    !isObject(value) ||
    Object.keys(value).some((key) => !fields.includes(key))
  ) {
    invalidResponse(`Post-approval Plaid response has an unsafe ${label}`);
  }
  return value;
};

const safeAmount = (value) => {
  const amount = safeObject(value, ['cents', 'currency_iso'], 'amount');
  if (
    (amount.cents !== undefined && !Number.isSafeInteger(amount.cents)) ||
    (amount.currency_iso !== undefined &&
      (typeof amount.currency_iso !== 'string' ||
        !/^[A-Z]{3}$/.test(amount.currency_iso)))
  ) {
    invalidResponse('Post-approval Plaid response has an invalid amount');
  }
  return { ...amount };
};

const safeEntitlement = (value) => {
  const entitlement = safeObject(
    value,
    ['approved_credit_limit', 'tier', 'effective_tcl'],
    'entitlement'
  );
  const result = {};
  if (entitlement.approved_credit_limit !== undefined) {
    result.approved_credit_limit = safeAmount(
      entitlement.approved_credit_limit
    );
  }
  if (entitlement.tier !== undefined) {
    if (
      typeof entitlement.tier !== 'string' ||
      !/^(?:[A-Z]{1,4}[0-9]{0,2}|unspecified|none)$/.test(entitlement.tier)
    ) {
      invalidResponse('Post-approval Plaid response has an invalid tier');
    }
    result.tier = entitlement.tier;
  }
  if (entitlement.effective_tcl !== undefined) {
    const tcl = safeObject(
      entitlement.effective_tcl,
      ['amount', 'remaining_amount', 'source'],
      'effective_tcl'
    );
    result.effective_tcl = {};
    ['amount', 'remaining_amount'].forEach((field) => {
      if (tcl[field] !== undefined)
        result.effective_tcl[field] = safeAmount(tcl[field]);
    });
    if (tcl.source !== undefined) {
      if (
        !['underwriting', 'override', 'underwriting_refresh'].includes(
          tcl.source
        )
      ) {
        invalidResponse(
          'Post-approval Plaid response has an invalid TCL source'
        );
      }
      result.effective_tcl.source = tcl.source;
    }
  }
  return result;
};

const safeAccount = (value) => {
  const account = safeObject(value, ['ready', 'mask'], 'account');
  if (account.ready != null && typeof account.ready !== 'boolean') {
    invalidResponse(
      'Post-approval Plaid response has an invalid account readiness'
    );
  }
  if (
    account.mask != null &&
    (typeof account.mask !== 'string' || !/^\d{1,4}$/.test(account.mask))
  ) {
    invalidResponse('Post-approval Plaid response has an unsafe account mask');
  }
  return { ready: account.ready === true, mask: account.mask || null };
};

const safeText = (value, label) => {
  if (value == null) return null;
  if (typeof value !== 'string' || !/^[a-z0-9_.]{1,128}$/.test(value)) {
    invalidResponse(`Post-approval Plaid response has an invalid ${label}`);
  }
  return value;
};

const safeAttemptId = (value) => {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(value)) {
    invalidResponse('Post-approval Plaid response has an invalid attempt_id');
  }
  return value;
};

const safeTimestamp = (value, label) => {
  if (value === null) return null;
  if (
    typeof value !== 'string' ||
    !/^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d\.\d{3}Z$/.test(
      value
    ) ||
    !Number.isFinite(Date.parse(value)) ||
    new Date(value).toISOString() !== value
  ) {
    invalidResponse(`Post-approval Plaid response has an invalid ${label}`);
  }
  return value;
};

export const statusResource = (applicationId, statusUrl) => {
  if (typeof statusUrl !== 'string') {
    return invalidResponse(
      'Post-approval Plaid response is missing status_url'
    );
  }

  let url;

  try {
    url = new URL(statusUrl, 'https://ck-web.creditkey.localhost');
  } catch {
    return invalidResponse(
      'Post-approval Plaid response has an invalid status_url'
    );
  }

  // ck-web is mounted at /app in hosted environments. Keep resources relative
  // to the configured API base so subsequent reads do not duplicate that mount.
  const resourcePath = url.pathname.startsWith('/app/api/')
    ? url.pathname.slice('/app'.length)
    : url.pathname;
  const expectedPath =
    applicationId === undefined
      ? /^\/api\/applications\/[^/]+\/post_approval_plaid$/
      : `/api/applications/${encodeURIComponent(
          applicationId
        )}/post_approval_plaid`;
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.hash ||
    url.search ||
    (typeof expectedPath === 'string'
      ? resourcePath !== expectedPath
      : !expectedPath.test(resourcePath))
  ) {
    return invalidResponse(
      'Post-approval Plaid response has an unsafe status_url'
    );
  }

  return { resource: resourcePath };
};

export const normalizeStatusResponse = (json, applicationId) => {
  if (!isObject(json)) {
    return invalidResponse('Post-approval Plaid response must be an object');
  }

  if (!Object.prototype.hasOwnProperty.call(json, 'status')) {
    if (!isPreAttemptResponse(json)) {
      return invalidResponse(
        'Post-approval Plaid response must be an attempt or pre-attempt response'
      );
    }

    return {
      kind: 'none',
      can_continue: json.can_continue,
      next_route_path: nextRoutePath(json.next_route),
    };
  }

  if (!STATUSES.includes(json.status)) {
    return invalidResponse(
      'Post-approval Plaid response has an unknown status'
    );
  }

  if (typeof json.can_continue !== 'boolean') {
    return invalidResponse(
      'Post-approval Plaid response has invalid can_continue'
    );
  }

  const attemptId = safeAttemptId(json.attempt_id);
  const submittedAt = safeTimestamp(json.submitted_at, 'submitted_at');
  const deadlineAt = safeTimestamp(json.deadline_at, 'deadline_at');
  const baseline = safeEntitlement(json.baseline);
  const effective = safeEntitlement(json.effective);
  const account = safeAccount(json.account);
  const company = safeObject(json.company, ['underwriting'], 'company');
  const underwriting = safeObject(
    company.underwriting,
    ['enhanced'],
    'underwriting'
  );
  if (
    underwriting.enhanced != null &&
    typeof underwriting.enhanced !== 'boolean'
  ) {
    invalidResponse('Post-approval Plaid response has invalid underwriting');
  }

  const { resource: statusUrl } = statusResource(
    applicationId,
    json.status_url
  );

  return {
    kind: 'attempt',
    attempt: new PostApprovalPlaidAttemptRecord({
      attempt_id: attemptId,
      status: json.status,
      status_url: statusUrl,
      submitted_at: submittedAt,
      deadline_at: deadlineAt,
      baseline,
      effective,
      company_underwriting_enhanced: underwriting.enhanced === true,
      account_ready: account.ready,
      account_mask: account.mask,
      error_code: safeText(json.error_code, 'error_code'),
      copy_key: safeText(json.copy_key, 'copy_key'),
      can_continue: json.can_continue === true,
      next_route_path: nextRoutePath(json.next_route),
    }),
  };
};

const defaultError = (status) => {
  switch (status) {
    case 401:
      return { outcome: 'unauthorized', reason_code: 'unauthorized' };
    case 409:
      return { outcome: 'conflict', reason_code: 'submission_conflict' };
    case 422:
      return { outcome: 'invalid_request', reason_code: 'invalid_request' };
    case 502:
      return { outcome: 'provider_error', reason_code: 'plaid_unavailable' };
    default:
      return { outcome: 'request_error', reason_code: 'request_error' };
  }
};

const safeError = (status, data) => {
  const fallback = defaultError(status);
  const allowedReasonCodes = {
    401: ['unauthorized'],
    409: ['submission_conflict'],
    422: [
      'invalid_request',
      'invalid_account',
      'application_required',
      'attempt_unavailable',
      'attempt_not_link_ready',
      'idempotency_key_required',
    ],
    502: ['plaid_unavailable'],
  };

  if (
    data.outcome !== undefined &&
    (typeof data.outcome !== 'string' || data.outcome !== fallback.outcome)
  ) {
    return fallback;
  }

  if (
    data.reason_code !== undefined &&
    (!allowedReasonCodes[status] ||
      !allowedReasonCodes[status].includes(data.reason_code))
  ) {
    return fallback;
  }

  return {
    outcome: fallback.outcome,
    reason_code: data.reason_code || fallback.reason_code,
  };
};

export const normalizeError = (axiosError) => {
  const response = axiosError && axiosError.response;

  if (!response) {
    const timeout = axiosError && axiosError.code === 'ECONNABORTED';

    return {
      http_status: null,
      outcome: 'network_error',
      reason_code: timeout ? 'timeout' : 'network_error',
      can_continue: false,
      next_route_path: null,
      network: true,
    };
  }

  const status = response.status;
  const data = isObject(response.data) ? response.data : {};
  const error = safeError(status, data);

  return {
    http_status: status,
    outcome: error.outcome,
    reason_code: error.reason_code,
    can_continue: data.can_continue === true,
    next_route_path: nextRoutePath(data.next_route),
    network: false,
  };
};

export const newIdempotencyKey = () => crypto.randomUUID();
